import type { ScenarioName, ScenarioSource, ScenarioStatus } from '@ms/yammer-scenario-telemetry';
import type { PageName } from '@ms/yammer-telemetry-support';

import type { PageLoadEventName } from './loadEvents';

/**
 * A navigation destination instrumented for scenario QoS telemetry, mapped to the
 * mobile-aligned scenario `name` and `source` it reports when its page load completes.
 */
export interface NavigationScenario {
  readonly name: ScenarioName;
  readonly source: ScenarioSource;
}

/**
 * Registry of page-load destinations instrumented for scenario QoS telemetry, keyed by the
 * page's telemetry `PageName`. Add a row to instrument another navigation scenario — the
 * provider-level hook picks it up automatically, so no per-page wiring is needed.
 *
 * Intentionally starts with only the home feed; community feed and conversation are added
 * once their cross-platform wire contracts are confirmed (see
 * `plans/scenario-based-qos-telemetry.md`).
 *
 * NOTE: `source` is fixed per destination for now. Threading the real entry point (deep
 * link vs. push notification vs. bottom nav) is deferred to the source fan-out follow-up.
 */
const navigationScenarioRegistry: Partial<Record<PageName, NavigationScenario>> = {
  HomeFeed: { name: 'open_home_feed', source: 'engage_left_nav' },
};

/**
 * Returns the scenario a page load should report, or `undefined` if the page is not
 * instrumented for scenario QoS telemetry.
 */
export const getNavigationScenarioForPage = (page: PageName): NavigationScenario | undefined =>
  navigationScenarioRegistry[page];

/**
 * Maps a terminal page-load outcome event to the mobile-aligned scenario status, so a
 * navigation scenario's reliability mirrors the page load it wraps. Recoverable and
 * unrecoverable failures both count as a failed load.
 */
export const pageLoadOutcomeToScenarioStatus: Record<PageLoadEventName, ScenarioStatus> = {
  Loaded: 'ok',
  TimedOut: 'timeout',
  Interrupted: 'cancel',
  Failed: 'failure',
  FailedRecoverable: 'failure',
};
